import React from "react"
import { flexRender, type ColumnDef, type Table as TanStackTable } from "@tanstack/react-table"
import { Search } from "lucide-react"
import { getColumnHeader, isActionsColumn } from "./crudTableUtils.ts"

type CrudMobileRowListProps<T> = {
  table: TanStackTable<T>
  columns: ColumnDef<T>[]
  isLoading?: boolean
}

export function CrudMobileRowList<T>({ table, columns, isLoading }: CrudMobileRowListProps<T>) {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center py-10 md:hidden">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-electric-blue border-t-transparent" />
        <p className="mt-3 text-sm text-slate-gray">Loading...</p>
      </div>
    )
  }

  const rows = table.getRowModel().rows
  const dataColumns = columns.filter((col) => !isActionsColumn(col))
  const actionsColumn = columns.find(isActionsColumn)

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-slate-gray/10 bg-white py-10 text-center md:hidden">
        <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-light-sky/50">
          <Search className="h-5 w-5 text-slate-gray/60" />
        </div>
        <p className="text-sm font-medium text-deep-ocean">No data found</p>
        <p className="mt-1 text-xs text-slate-gray">Try adjusting your search or filters</p>
      </div>
    )
  }

  return (
    <div className="space-y-2 md:hidden">
      {rows.map((row) => {
        const dataCells = row
          .getVisibleCells()
          .filter((cell) => !isActionsColumn(cell.column.columnDef as ColumnDef<T>))

        const actionsCell = actionsColumn
          ? row.getVisibleCells().find((cell) => isActionsColumn(cell.column.columnDef as ColumnDef<T>))
          : undefined

        const primaryCell = dataCells[0]
        const subtitleCell = dataCells[1]
        const metaCells = dataCells.slice(2)

        return (
          <div
            key={row.id}
            className={`rounded-lg border bg-white p-3 shadow-sm ${
              row.getIsSelected()
                ? "border-electric-blue/40 bg-electric-blue/[0.04]"
                : "border-slate-gray/15"
            }`}
          >
            <div className="flex items-start gap-2.5">
              <input
                type="checkbox"
                checked={row.getIsSelected()}
                onChange={row.getToggleSelectedHandler()}
                className="mt-1 h-[18px] w-[18px] shrink-0 rounded border-slate-gray/30 text-electric-blue focus:ring-electric-blue/50"
                aria-label="Select row"
              />
              <div className="min-w-0 flex-1">
                {primaryCell ? (
                  <div className="text-sm font-semibold leading-snug text-deep-ocean [&_*]:text-inherit [&_*]:font-inherit">
                    {flexRender(primaryCell.column.columnDef.cell, primaryCell.getContext())}
                  </div>
                ) : null}
                {subtitleCell ? (
                  <div className="mt-0.5 text-sm text-slate-gray [&_*]:text-inherit">
                    {flexRender(subtitleCell.column.columnDef.cell, subtitleCell.getContext())}
                  </div>
                ) : null}
              </div>
            </div>

            {metaCells.length > 0 ? (
              <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-slate-100 pt-2">
                {metaCells.map((cell) => (
                  <div key={cell.id} className="min-w-0">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-slate-gray/80">
                      {getColumnHeader(cell.column.columnDef as ColumnDef<T>)}
                    </p>
                    <div className="truncate text-xs text-slate-gray [&_*]:text-inherit">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            {actionsCell ? (
              <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-100 pt-3 [&_button]:!h-10 [&_button]:!min-h-10 [&_button]:!min-w-10 [&_a]:!inline-flex [&_a]:!h-10 [&_a]:!min-h-10 [&_a]:!items-center [&_a]:!px-3">
                {flexRender(actionsCell.column.columnDef.cell, actionsCell.getContext())}
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
